import { useCallback, useContext, useMemo, useRef } from 'react'

import {
  Button,
  Dialog,
  HStack,
  IconButton,
  Portal,
  Separator,
  Spacer,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { AddIcon } from '@editor/assets/icons'
import { INFO_PANEL_PADDING } from '@editor/config/styles'
import { UserContext } from '@editor/contexts/Users'
import { EditorColoringType } from '@editor/interfaces'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { InspectionGroup } from '@editor/interfaces/inspectionGroup'
import { ERROR_PROCESS, getCommonErrorMessage } from '@editor/services/ErrorHandler'
import { postInspectionGroup, validateName } from '@editor/services/InspectionGroup'
import { withPromiseContained } from '@editor/services/Util'
import { GenericError } from '@editor/services/errors/GenericError'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { getDistinctVibrantColor } from '@infraloop/services'

import { patchEditorDisplaySettings } from '../store/editorSettings'
import {
  addInspectionGroup,
  patchInspectionGroup,
  replaceSavedInspectionGroup,
  updateInspectionGroup,
} from '../store/inspectionGroup'
import { Layer } from './components/Layer'
import useDragNDrop from './hooks/useDragNDrop'
import { cleanEditorPanelItem } from './utils'

interface InspectionGroupComponentProps {
  hideTitle?: boolean
  groupLayers: EditorPanelItem[]
  isAllowedToModify: boolean
  depth?: number
  onCheckToggled?: (ids: string[], selected: boolean) => void

  /**
   * Custom function to handle visibility toggling.
   *
   * @param ids The IDs of the items to toggle visibility for.
   * @param invisible Whether to set the items as invisible (true) or visible (false).
   */
  onVisibilityToggled?: (ids: string[], invisible: boolean) => void

  /**
   * Custom function to handle element updates (for child inspection items).
   */
  onElementUpdated?: (item: EditorPanelItem) => void
}

const InspectionGroupComponent = ({
  hideTitle = false,
  depth = 0,
  groupLayers,
  isAllowedToModify,
  onCheckToggled,
  onVisibilityToggled,
  onElementUpdated,
}: InspectionGroupComponentProps) => {
  const { open, onOpen, onClose } = useDisclosure()

  // Context
  const { getAccessToken } = useContext(UserContext)

  // Refs
  const groupsContainerRef = useRef<HTMLDivElement>(null)
  const groupAddedFromDialogRef = useRef(false)

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionGroups = useAppSelector((state) => state.inspectionGroup.groups)
  const draggingItemKey = useAppSelector((state) => state.inspectionGroup.draggingItemKey)
  const displayType = useAppSelector((state) => state.editorSettings.display.general.display_type)

  const draggedItem = useMemo(
    () => inspectionGroups.find((group) => group.key === draggingItemKey),
    [draggingItemKey, inspectionGroups],
  )

  // Permission check
  const canAdd = inspectionGroups.every(
    (group) => !group.isWorking || (group.isWorking && (group.isSaving || group.errorMessage)),
  )

  // Hooks
  useDragNDrop(groupsContainerRef.current)

  /**
   * Add an empty inspection group
   */
  const addEmptyGroup = useCallback(() => {
    // REMINDER: Don't store any function callbacks here. Redux don't like it.
    dispatch(
      addInspectionGroup({
        key: crypto.randomUUID(),
        name: '',
        color: getDistinctVibrantColor(inspectionGroups.map((g) => g.color).filter(Boolean) as string[]),
        order: Math.max(...inspectionGroups.map((g) => g.order || 0), 0) + 1,
        isEditing: true,
        isWorking: true,
        isDirty: true,
      }),
    )
  }, [dispatch, inspectionGroups])

  /**
   * Handle adding a new group
   *
   * @param e The click event
   */
  const handleAddGroup = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()

      if (displayType === EditorColoringType.Auto && !inspectionGroups.length) {
        groupAddedFromDialogRef.current = false
        onOpen()
        return
      }

      addEmptyGroup()
    },
    [inspectionGroups, displayType, onOpen, addEmptyGroup],
  )

  /**
   * Handle group update
   * This function is called when the group is updated, either after
   * editing a new group or updating an existing one.
   *
   * @param item The group item to be updated
   */
  const handleGroupUpdate = useCallback(
    async (item: EditorPanelItem) => {
      if (!inspectionArea || !project) {
        return
      }

      // If all we want is to edit it, just update the item in the store
      if (item.isEditing) {
        dispatch(updateInspectionGroup(cleanEditorPanelItem(item)))
        return
      }

      // Validate the name if it is being changed or none is provided
      const oldItem = inspectionGroups.find((g) => g.key === item.key)
      if (
        typeof validateName(
          item.name,
          item.key,
          inspectionGroups.filter((g) => g.key !== item.key),
        ) === 'string'
      ) {
        return
      }

      // Compare old and new item, if nothing changed, just return
      // We'll only check for name, color, and order. The rest are not editable.
      if (
        oldItem &&
        oldItem.name === item.name &&
        oldItem.color === item.color &&
        oldItem.order === item.order &&
        !oldItem.isDirty
      ) {
        dispatch(updateInspectionGroup({ key: item.key, isEditing: false }))
        return
      }

      await withPromiseContained(async () => {
        if (item.color === undefined || item.order === undefined) return

        const token = await getAccessToken()
        if (!token) throw new Error('Failed to get access token')

        dispatch(
          updateInspectionGroup(
            cleanEditorPanelItem({
              ...item,
              isEditing: false,
              isSaving: true,
              onDelete: undefined,
            }),
          ),
        )

        // Save it
        if (item.isWorking) {
          const result = await postInspectionGroup(
            token,
            project.project_id,
            inspectionArea.inspection_area_id,
            item.name,
            item.color,
            item.order,
          )

          dispatch(
            replaceSavedInspectionGroup({
              oldKey: item.key,
              item: cleanEditorPanelItem({
                ...item,
                key: `panel-group-${result.group_id}`,
                item: result,
                isOrderable: isAllowedToModify,
                isCollapsible: true,
                isWorking: false,
                isEditing: false,
                isSaving: false,
                isDirty: false,
                onDelete: undefined,
              }),
            }),
          )
        } else {
          // Just add to edited list, it will be saved later
          dispatch(patchInspectionGroup({ key: item.key, isSaving: false, isDirty: false }))
        }
      }).catch((error) => {
        dispatch(
          updateInspectionGroup({
            key: item.key,
            isSaving: false,
            isDirty: false,
            errorMessage:
              error instanceof GenericError ? error.message : getCommonErrorMessage(ERROR_PROCESS.INSPECTION_GROUP),
          }),
        )
      })
    },
    [project, inspectionArea, inspectionGroups, isAllowedToModify, getAccessToken, dispatch],
  )

  /**
   * Handle color change for a group.
   * This should only update store, not the server.
   * A separate update call will be made when it is time to save to server.
   *
   * Done separately so we can see the changes in real-time without
   * spamming the server with requests.
   *
   * @param key The key of the group to be updated
   * @param color The new color for the group
   */
  const handleColorChange = useCallback(
    (key: string, color: string) => {
      dispatch(
        patchInspectionGroup({
          key,
          color,
          isDirty: true,
          item: {
            color,
          } as InspectionGroup,
        }),
      )
    },
    [dispatch],
  )

  /**
   * Handle updates to items in a group.
   * Routes to appropriate handler based on whether the group itself or a child element is being updated.
   *
   * @param groupKey The key of the group being rendered
   * @param updatedItem The item that was updated
   */
  const handleItemUpdate = useCallback(
    (groupKey: string, updatedItem: EditorPanelItem) => {
      // If updating the group itself, use handleGroupUpdate
      // If updating a child element, use onElementUpdated
      if (updatedItem.key === groupKey) {
        void handleGroupUpdate(updatedItem)
      } else if (onElementUpdated) {
        void onElementUpdated(updatedItem)
      }
    },
    [handleGroupUpdate, onElementUpdated],
  )

  return (
    <VStack w="100%" gap={0} pb={INFO_PANEL_PADDING - 1} ref={groupsContainerRef}>
      {!hideTitle && (
        <HStack
          w="100%"
          px={2}
          py={1}
          backgroundColor="gray2.800"
          borderBottomColor="gray2.900"
          borderBottomWidth={1}
          cursor="pointer"
          position="sticky"
          top={0}
          left={0}
          zIndex={8}
        >
          <Text flex={1} whiteSpace="nowrap">
            検査グループ
          </Text>
          <Spacer />
          {isAllowedToModify ? (
            <IconButton
              aria-label="add group"
              cursor="pointer"
              variant="panel-icon"
              onClick={handleAddGroup}
              disabled={!canAdd}
            >
              <AddIcon />
            </IconButton>
          ) : null}
        </HStack>
      )}
      {/* Group list */}
      {groupLayers.map((group) => (
        <Layer
          key={group.key}
          item={group}
          depth={depth + (isAllowedToModify ? 0 : -1)}
          editMode={group.isEditing}
          onUpdated={isAllowedToModify ? (updatedItem) => handleItemUpdate(group.key, updatedItem) : undefined}
          onColorChanged={isAllowedToModify ? (color: string) => handleColorChange(group.key, color) : undefined}
          onCheckToggled={onCheckToggled}
          onVisibilityToggled={onVisibilityToggled}
          validateName={(name, targetId) => validateName(name, targetId, inspectionGroups)}
          forGroup
        />
      ))}
      {/* Shadow clone, dummy for drag preview */}
      {draggedItem && (
        <Portal>
          <Layer key={`${draggedItem.key}-shadow-clone`} item={draggedItem} depth={0} isShadowClone />
        </Portal>
      )}
      <Dialog.Root
        open={open}
        size="xl"
        // The dialog restores focus to its trigger after its exit completes,
        // which is after the new group's name input has taken focus via
        // autoFocus. When a group was added from the dialog, redirect that
        // restore to the input instead; on cancel/close, null falls back to
        // the default restore-to-trigger behaviour.
        finalFocusEl={() =>
          groupAddedFromDialogRef.current
            ? (groupsContainerRef.current?.querySelector<HTMLInputElement>('input[data-autocomplete-input]') ?? null)
            : null
        }
        onOpenChange={(e) => {
          if (!e.open) {
            onClose()
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                要素の色付け動作の変更
                <Dialog.CloseTrigger />
              </Dialog.Header>
              <Dialog.Body>
                <Text>
                  現在の自動色付け設定では、検査グループを追加すると色付け動作が要素の種類の色から検査グループの色に切り替わります。
                </Text>
                <br />
                <Text>
                  検査グループの色モードでは、要素はグループに基づいて色付けされるか、グループに含まれない場合は未分類用の色が使用されます。
                </Text>
                <br />
                <Text>
                  自動切り替えを実行しますか？それとも要素の種類による色のままにしますか？この設定は画面設定からいつでも変更できます。
                </Text>
                <br />
                <Text>どちらのオプションでも検査グループを追加できますが、選択に応じて色付け動作が異なります。</Text>
              </Dialog.Body>
              <Separator />
              <Dialog.Footer gap={2}>
                <Button variant="plain" onClick={onClose}>
                  キャンセル
                </Button>
                <Spacer />
                <Button
                  onClick={() => {
                    dispatch(patchEditorDisplaySettings({ general: { display_type: EditorColoringType.Element } }))
                    setTimeout(() => document.dispatchEvent(new CustomEvent('inspection-area:patch')))

                    groupAddedFromDialogRef.current = true
                    addEmptyGroup()
                    onClose()
                  }}
                >
                  要素の種類による色のまま
                </Button>
                <Button
                  colorPalette="blue"
                  onClick={() => {
                    groupAddedFromDialogRef.current = true
                    addEmptyGroup()
                    onClose()
                  }}
                >
                  検査グループの色に切り替え
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </VStack>
  )
}

export default InspectionGroupComponent
